import { useMutation, useQuery } from "@tanstack/react-query";
import type { EffectiveVisibleSections } from "@shared/lib/visibleSections";
import { apiClient, ApiError } from "./client";

export interface ScopedAllergy {
  id: string;
  name: string;
  severity: "mild" | "moderate" | "severe";
  description: string | null;
  emergencyAction: string | null;
  reactionSymptoms: string | null;
  treatment: string | null;
  epinephrineLocation: string | null;
  /**
   * Present only when the MEDICATIONS section is also shared — the server omits
   * the key entirely otherwise (see scopeStripper). Optional, not nullable, so
   * "not shared" and "none linked" stay distinguishable.
   */
  rescueMedicationName?: string | null;
  notes: string | null;
}

export interface ScopedAuthorizedPickup {
  id: string;
  name: string;
  relationship: string | null;
  phone: string | null;
  /** Absent while PHOTO_UPLOADS_ENABLED is false: care links carry no images. */
  photoUrl?: string | null;
  /** false = listed but revoked. Rendered as "not authorized", never hidden. */
  isActive: boolean;
  notes: string | null;
}

export interface ScopedCareAuthorization {
  consentToTreat: boolean;
  consentGrantedByName: string | null;
  consentGrantedAt: string | null;
  insuranceCarrier: string | null;
  insurancePolicyNumber: string | null;
  insuranceGroupNumber: string | null;
  preferredHospital: string | null;
  notes: string | null;
}

export interface ScopedMedication {
  id: string;
  name: string;
  dosage: string;
  frequency: string;
  expiresAt: string | null;
  notes: string | null;
}

export interface ScopedCarePreferences {
  behavioralNotes: string | null;
  calmingStrategies: string | null;
  comfortItems: string | null;
  communicationStyle: string | null;
}

export interface ScopedRoutine {
  id: string;
  label: string;
  daypart: "morning" | "midday" | "afternoon" | "evening" | "bedtime";
  category: "nap" | "meal" | "activity" | "medication" | "bedtime" | "other";
  timeText: string | null;
  details: string | null;
  notes: string | null;
}

export interface ScopedFeeding {
  dietType: string | null;
  restrictions: string | null;
  likes: string | null;
  dislikes: string | null;
  portionNotes: string | null;
  scheduleNotes: string | null;
  bottleFormula: string | null;
  utensilsNotes: string | null;
}

export interface ScopedMedicalHistory {
  primaryPhysicianName: string | null;
  primaryPhysicianPhone: string | null;
  dentistName: string | null;
  dentistPhone: string | null;
  bloodType: string | null;
  conditions: string | null;
  pastIllnesses: string | null;
  hospitalizations: string | null;
  insuranceNotes: string | null;
  notes: string | null;
}

export interface ScopedImmunization {
  id: string;
  vaccineName: string;
  dateAdministered: string | null;
  nextDueDate: string | null;
  provider: string | null;
  notes: string | null;
}

export interface ScopedChildView {
  /** What the link opens today: `pickup` is absent while the switch is off. */
  visibleSections: EffectiveVisibleSections;
  child: {
    /** The nickname. Always present. */
    name: string;
    /** Present only when the parent ticked "Include legal name" on this link. */
    legalName?: string | null;
    /** Present only while PHOTO_UPLOADS_ENABLED is true. */
    photoUrl?: string | null;
    age?: number | null;
    grade?: string | null;
    additionalNotes?: string | null;
    isEnrolled?: boolean;
    schoolName?: string | null;
    schoolPhone?: string | null;
    teacherName?: string | null;
    schoolNursePhone?: string | null;
  };
  emergencyContacts?: {
    primary: { name: string | null; phone: string | null; relationship: string | null };
    secondary: { name: string | null; phone: string | null; relationship: string | null };
  };
  allergies?: ScopedAllergy[];
  medications?: ScopedMedication[];
  carePreferences?: ScopedCarePreferences;
  routines?: ScopedRoutine[];
  feeding?: ScopedFeeding;
  medicalHistory?: ScopedMedicalHistory;
  immunizations?: ScopedImmunization[];
  authorizedPickups?: ScopedAuthorizedPickup[];
  careAuthorization?: ScopedCareAuthorization;
  meta: {
    caregiverName: string;
    lastUpdated: string;
    acknowledged: boolean;
  };
}

export function usePublicShare(token: string | undefined) {
  return useQuery<ScopedChildView, ApiError>({
    queryKey: ["public-share", token ?? "__none__"],
    queryFn: () => {
      if (!token) throw new Error("usePublicShare: token required");
      return apiClient<ScopedChildView>(`/api/public/share/${encodeURIComponent(token)}`);
    },
    enabled: Boolean(token),
    retry: false,
  });
}

/** Caregiver confirms they have read the care sheet (public, no auth). */
export function useAcknowledgeShare(token: string | undefined) {
  return useMutation<{ acknowledged: true }, ApiError, void>({
    mutationFn: () => {
      if (!token) throw new Error("useAcknowledgeShare: token required");
      return apiClient<{ acknowledged: true }>(
        `/api/public/share/${encodeURIComponent(token)}/acknowledge`,
        { method: "POST" },
      );
    },
  });
}
